import React, { useCallback, useEffect, useState } from 'react';
import toast, { ToastBar, Toaster } from 'react-hot-toast';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { SecureRoute, Security } from '@okta/okta-react';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import cn from 'classnames';
import produce, { setAutoFreeze } from 'immer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './App.scss';
import { ErrorBoundary, ErrorMessage, LoaderVPN, Navbar, RequireRole, Sidebar } from 'components';
import { AsyncStatus, UserRole } from 'enums';
import { Account, CreateUpdateFile, CustomLoginCallback, Home, Login, Manufacturers, Models, UpdateTypes, Page404, Payload, Reports, Stats, Update, UpdateFiles, LocationGroups, Approvers } from 'pages';
import { axiosAPI, LocationGroupsService, LocationsService, ManufacturersService, parseAxiosError, TemplatesService, UpdateFilesService, UpdateTypesService, UsersService } from 'services';
import { useDisableMainContainerScroll } from 'hooks';
import { getUpdateResultsURL } from 'pages/Reports/ReportsModel';
import { Templates } from 'pages/Templates/Templates';

// We sometimes pass mutable objects down the component tree which shouldn't be frozen.
setAutoFreeze(false);

const oktaAuth = new OktaAuth({
    // Note: If your app is configured to use the Implicit Flow
    // instead of the Authorization Code with Proof of Code Key Exchange (PKCE)
    // you will need to add `pkce: false`
    issuer: process.env.REACT_APP_OKTA_ISSUER,
    clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
    redirectUri: window.location.origin + '/login/callback',
    // pkce: false,
    tokenManager: {
        autoRenew: true,
        expireEarlySeconds: 60,
    }
});

export const oktaScopes = ['openid', 'email', 'profile'];

// Subscribe to changes in the auth state so that we can add the token to our headers after login.
// These must be set before rendering any component that loads data. This works
// because SecureRoute waits for authState to settle before rendering the route.
oktaAuth.authStateManager.subscribe((authState) => {
    const token = authState?.accessToken?.accessToken;

    if (token) {
        const headerVal = 'Bearer ' + token;
        axiosAPI.defaults.headers.common['Authorization'] = headerVal;
        UsersService.parseOktaUserInfo(authState);
    }
});

interface IAppState {
    isAuthenticated: boolean,
    loadError?: string,
    loadPromise?: Promise<any>,
    loadStatus: AsyncStatus,
}

export function App(props) {
    const history = useHistory();
    const { isScrollDisabled } = useDisableMainContainerScroll();
    const [{
        isAuthenticated,
        loadError,
        loadPromise,
        loadStatus,
    }, setState] = useState<IAppState>({
        isAuthenticated: false,
        loadStatus: AsyncStatus.Initial,
    });

    // We can't use the "useAuthState" hook here because we're outside the Security component.
    // So, to get the auth state, we'll manually subscribe.
    const onChangeAuthState = useCallback((authState) => {
        setState(produce(draft => {
            draft.isAuthenticated = authState?.isAuthenticated;
        }));
    }, []);

    useEffect(() => {
        oktaAuth.authStateManager.subscribe(onChangeAuthState);
        return () => {
            oktaAuth.authStateManager.unsubscribe(onChangeAuthState);
        }
    })

    // Load all the data that's commonly used across all pages. This is better than having to load these on every page.
    useEffect(() => {
        if (!isAuthenticated || loadStatus !== AsyncStatus.Initial) return;

        // These are singletons that cache this data, so we just need to tell them to load.
        const manufacturersP = ManufacturersService.getManufacturers();
        const modelsP = ManufacturersService.getModels();
        const updateTypesP = UpdateTypesService.getUpdateTypes();
        const userP = UsersService.getAndCacheMe();
        const locationP = LocationsService.getAllLocations();
        const updateFilesP = UpdateFilesService.getAllUpdateFiles()
        const locationGroupsP = LocationGroupsService.getAllLocationGroups();
        const templatesP = TemplatesService.getAllTemplates();

        const p = Promise.all([
            manufacturersP, 
            modelsP, 
            updateTypesP, 
            userP, 
            locationP, 
            locationGroupsP, 
            templatesP, 
            updateFilesP,
        ]);

        setState(produce(draft => {
            draft.loadPromise = p;
            draft.loadStatus = AsyncStatus.Loading;
        }))

        p.then(() => {
            setState(produce(draft => {
                draft.loadStatus = AsyncStatus.Success;
            }))
        }).catch((err) => {
            setState(produce(draft => {
                draft.loadStatus = AsyncStatus.Error;
                draft.loadError = parseAxiosError(err);
            }))
        });

    }, [isAuthenticated, loadStatus])

    const onAuthRequired = () => {
        history.push('/login');
    }

    const restoreOriginalUri = async (_oktaAuth, originalUri) => {
        history.replace(toRelativeUrl(originalUri, window.location.origin));
    };

    return (
        <ErrorBoundary>
            <Security oktaAuth={oktaAuth} onAuthRequired={onAuthRequired} restoreOriginalUri={restoreOriginalUri}>
                {/* Override toaster default styles */}
                <Toaster
                    containerClassName= 'toast-container'
                    toastOptions={{
                        position: 'top-right',
                        success: {
                            style: {
                                background: '#d6ffee',
                                borderColor: '#073522',
                                color: '#073522',
                            },
                        },
                        error: {
                            style: {
                                background: '#ffd8e0',
                                borderColor: '#2b0009',
                                color: '#2b0009',
                            }
                        }
                    }}
                >
                    {(t) => (
                        <ToastBar toast={t}>
                            {({ icon, message }) => (<>
                                <div className="toast-content">
                                    <div className="toast-icon">{icon}</div>
                                    <span>{message}</span>
                                </div>
                                {/* To add a close button, we must return the toast object and add custom styling */}
                                {t.type !== 'loading' && (
                                    <button className="toast-close" onClick={() => toast.dismiss(t.id)}>
                                        <FontAwesomeIcon icon="times"></FontAwesomeIcon>
                                    </button>
                                )}
                            </>)}
                        </ToastBar>
                    )}
                </Toaster>
                <div className="mainContainer">
                    <Navbar className="navbar" />
                    {isAuthenticated && (
                        <Sidebar className="sidebar" />
                    )}
                    <div
                        className={cn('mainContent', {
                            disableScroll: isScrollDisabled,
                        })}
                    >
                        <Switch>
                            <Route path='/login/callback' exact component={CustomLoginCallback} />
                            <Route path='/login' exact component={Login} />
                            {loadStatus === AsyncStatus.Loading && <LoaderVPN loadPromise={loadPromise} />}
                            {loadStatus === AsyncStatus.Error && <ErrorMessage message={loadError} />}
                            {/* Note: Using a nested Switch here to make the 404 page work */}
                            {loadStatus === AsyncStatus.Success && <Switch>
                                <SecureRoute path='/' exact component={Home} />
                                <SecureRoute path='/updates/:id' exact component={Update} />
                                <SecureRoute path='/account' exact component={Account} />
                                <SecureRoute path='/reports' exact component={Reports} />
                                <SecureRoute path='/admin' exact render={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <Stats />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/manufacturers' exact render={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <Manufacturers />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/models' exact render={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <Models />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/updatetypes' exact render={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <UpdateTypes />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/updatefiles' exact render={() => (
                                    <RequireRole role={UserRole.FullUser}>
                                        <UpdateFiles />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/updatefiles/new' exact render={() => (
                                    <RequireRole role={UserRole.FullUser}>
                                        <CreateUpdateFile />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/locationGroups' exact render={() => (
                                    <RequireRole role={UserRole.FullUser}>
                                        <LocationGroups />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/payload' exact render={() => (
                                    <RequireRole role={UserRole.LimitedUser}>
                                        <Payload />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/approvers' exact render={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <Approvers />
                                    </RequireRole>
                                )} />
                                <SecureRoute path='/templates' exact render={() => (
                                    <RequireRole role={UserRole.Admin}>
                                        <Templates />
                                    </RequireRole>
                                )} />
                                <SecureRoute component={Page404} />
                            </Switch>}
                        </Switch>
                        {/* We're providing clean urls that we can use in emails,
                            since the reports URLs have a lot of params and may change in the future. */}
                        <Route path="/reports/update/:id" exact render={({ match }) => {
                            const url = getUpdateResultsURL({
                                updateId: parseInt(match.params.id, 10),
                                // Default to showing snapshot results
                                isSnapshot: true,
                            });

                            return <Redirect to={url} />
                        }} />
                        <Route path="/reports/update/:id/stageIndex/:stageIndex" exact render={({ match }) => {
                            const url = getUpdateResultsURL({
                                updateId: parseInt(match.params.id, 10),
                                stageIndex: parseInt(match.params.stageIndex, 10),
                                // Default to showing snapshot results
                                isSnapshot: true,
                            });

                            return <Redirect to={url} />
                        }} />
                        {/* Since we're only rendering most routes on load success, we need a secure wildcard route
                        to make it go to the login page if we're not authenticated. */}
                        <SecureRoute path='*' />
                    </div>
                </div>
            </Security>
        </ErrorBoundary>
    );
}